import {
  DEFAULT_WALL_COLOR,
  DEFAULT_WALL_THICKNESS_M,
} from './floorPlanDesignerState';

const DEFAULT_MAX_WIDTH_M = 30;
const DEFAULT_MAX_HEIGHT_M = 30;
const DEFAULT_PADDING_M = 1;
const DEFAULT_STANDARD_DOOR_WIDTH_M = 1.0;
const DEFAULT_MIN_DOOR_WIDTH_PX = 40;
const DEFAULT_MAX_DOOR_WIDTH_PX = 160;

function finitePositiveNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : null;
}

function getImageSizeFromCoordinateSystem(coordinateSystem) {
  return {
    imageWidth: finitePositiveNumber(coordinateSystem?.imageWidth),
    imageHeight: finitePositiveNumber(coordinateSystem?.imageHeight),
  };
}

function getImageSize(response, coordinateSystem = {}) {
  const coordinateImageSize = getImageSizeFromCoordinateSystem(coordinateSystem);
  const imageWidth =
    finitePositiveNumber(response?.imageSize?.width) ||
    coordinateImageSize.imageWidth;
  const imageHeight =
    finitePositiveNumber(response?.imageSize?.height) ||
    coordinateImageSize.imageHeight;

  return {
    imageWidth: imageWidth || 1,
    imageHeight: imageHeight || 1,
  };
}

export function resolveCubiCasaPixelGeometry(response) {
  const hasTopLevelGeometry = Array.isArray(response?.points) && Array.isArray(response?.walls);
  if (hasTopLevelGeometry) {
    return {
      rawPoints: response.points,
      rawWalls: response.walls,
      rawOpenings: Array.isArray(response?.openings) ? response.openings : [],
      coordinateSystem: response?.coordinateSystem || {},
    };
  }

  const vectorGeometry = response?.vectorGeometry;
  if (Array.isArray(vectorGeometry?.points) && Array.isArray(vectorGeometry?.walls)) {
    return {
      rawPoints: vectorGeometry.points,
      rawWalls: vectorGeometry.walls,
      rawOpenings: Array.isArray(vectorGeometry?.detectedOpenings)
        ? vectorGeometry.detectedOpenings
        : [],
      coordinateSystem: vectorGeometry?.coordinateSystem || {},
    };
  }

  return {
    rawPoints: [],
    rawWalls: [],
    rawOpenings: [],
    coordinateSystem: {},
  };
}

export function getMaxPointCoordinate(points = []) {
  if (!points.length) return 0;

  return Math.max(
    ...points.flatMap((point) => [
      Math.abs(Number(point.x) || 0),
      Math.abs(Number(point.y) || 0),
    ]),
  );
}

export function looksNormalized(points) {
  if (!points?.length) return false;

  return getMaxPointCoordinate(points) <= 2;
}

export function getPointBounds(points = []) {
  if (!points.length) return null;

  const xs = points.map((point) => Number(point.x)).filter(Number.isFinite);
  const ys = points.map((point) => Number(point.y)).filter(Number.isFinite);
  if (!xs.length || !ys.length) return null;

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    minX,
    maxX,
    minY,
    maxY,
    widthM: maxX - minX,
    heightM: maxY - minY,
  };
}

export function getMeterBoundsFromPointBounds(bounds, metersPerPixel) {
  if (!bounds || !Number.isFinite(metersPerPixel) || metersPerPixel <= 0) return null;

  return {
    minX: bounds.minX * metersPerPixel,
    maxX: bounds.maxX * metersPerPixel,
    minY: bounds.minY * metersPerPixel,
    maxY: bounds.maxY * metersPerPixel,
    widthM: bounds.widthM * metersPerPixel,
    heightM: bounds.heightM * metersPerPixel,
  };
}

export function getValidDoorWidths(openings = [], options = {}) {
  const minDoorWidthPx = options.minDoorWidthPx ?? DEFAULT_MIN_DOOR_WIDTH_PX;
  const maxDoorWidthPx = options.maxDoorWidthPx ?? DEFAULT_MAX_DOOR_WIDTH_PX;

  return openings
    .filter((opening) => opening?.type === 'door')
    .map((opening) => Number(opening.widthPixels))
    .filter(
      (width) =>
        Number.isFinite(width) &&
        width >= minDoorWidthPx &&
        width <= maxDoorWidthPx,
    );
}

export function calculateDoorScale(openings = [], options = {}) {
  const standardDoorWidthM = options.standardDoorWidthM ?? DEFAULT_STANDARD_DOOR_WIDTH_M;
  const doorWidths = getValidDoorWidths(openings, options);

  if (doorWidths.length === 0) {
    return null;
  }

  const averageDoorWidthPx =
    doorWidths.reduce((sum, width) => sum + width, 0) / doorWidths.length;

  return {
    pixelsPerMeter: averageDoorWidthPx / standardDoorWidthM,
    averageDoorWidthPx,
    detectedDoorCount: doorWidths.length,
    source: 'average-door-width-1m',
  };
}

export function calculatePixelsPerMeterFromDoors(openings = [], options = {}) {
  return calculateDoorScale(openings, options)?.pixelsPerMeter ?? null;
}

export function calculatePixelsPerMeterByFit({
  imageWidth,
  imageHeight,
  maxWidthM = DEFAULT_MAX_WIDTH_M,
  maxHeightM = DEFAULT_MAX_HEIGHT_M,
  paddingM = DEFAULT_PADDING_M,
}) {
  const width = finitePositiveNumber(imageWidth) || 1;
  const height = finitePositiveNumber(imageHeight) || 1;
  const usableWidthM = Math.max(1, maxWidthM - paddingM * 2);
  const usableHeightM = Math.max(1, maxHeightM - paddingM * 2);

  const ppmX = width / usableWidthM;
  const ppmY = height / usableHeightM;

  return Math.max(ppmX, ppmY);
}

export function calculatePixelsPerMeter(params) {
  return calculatePixelsPerMeterByFit(params);
}

function pixelPointToDesignerPoint(point, offsetXPx = 0, offsetYPx = 0) {
  const x = Number(point.x);
  const y = Number(point.y);

  return {
    ...point,
    x: x + offsetXPx,
    y: y + offsetYPx,
  };
}

function normalizeWallForDesigner(wall) {
  return {
    ...wall,
    thicknessMeters: wall.thicknessMeters ?? DEFAULT_WALL_THICKNESS_M,
    color: wall.color ?? DEFAULT_WALL_COLOR,
  };
}

export function convertCubiCasaImportToDesignerState(response, options = {}) {
  const {
    maxWidthM = DEFAULT_MAX_WIDTH_M,
    maxHeightM = DEFAULT_MAX_HEIGHT_M,
    paddingM = DEFAULT_PADDING_M,
    offsetX = 1,
    offsetY = 1,
    standardDoorWidthM = DEFAULT_STANDARD_DOOR_WIDTH_M,
    minDoorWidthPx = DEFAULT_MIN_DOOR_WIDTH_PX,
    maxDoorWidthPx = DEFAULT_MAX_DOOR_WIDTH_PX,
  } = options;

  const { rawPoints, rawWalls, rawOpenings, coordinateSystem } =
    resolveCubiCasaPixelGeometry(response);
  const { imageWidth, imageHeight } = getImageSize(response, coordinateSystem);
  const maxRawCoord = getMaxPointCoordinate(rawPoints);

  if (process.env.NODE_ENV === 'development') {
    console.log('CubiCasa coordinate debug', {
      coordinateUnit: coordinateSystem.unit,
      imageWidth: coordinateSystem.imageWidth,
      imageHeight: coordinateSystem.imageHeight,
      firstRawPoint: rawPoints[0],
      maxRawCoord,
      openingsCount: rawOpenings.length,
    });
  }

  if (looksNormalized(rawPoints)) {
    console.error(
      'CubiCasa import received normalized coordinates, not pixel coordinates. Do not import walls_clean/wall_segments_for_render.',
    );
    throw new Error(
      'Invalid CubiCasa import: expected pixel coordinates, received normalized coordinates.',
    );
  }

  const doorScale = calculateDoorScale(rawOpenings, {
    standardDoorWidthM,
    minDoorWidthPx,
    maxDoorWidthPx,
  });
  const pixelsPerMeter =
    doorScale?.pixelsPerMeter ||
    calculatePixelsPerMeterByFit({
      imageWidth,
      imageHeight,
      maxWidthM,
      maxHeightM,
      paddingM,
    });
  const scaleSource = doorScale?.source || 'auto-fit-to-designer-area';
  const metersPerPixel = 1 / pixelsPerMeter;
  const offsetXPx = offsetX * pixelsPerMeter;
  const offsetYPx = offsetY * pixelsPerMeter;
  const validDoorWidths = getValidDoorWidths(rawOpenings, {
    minDoorWidthPx,
    maxDoorWidthPx,
  });
  const averageDoorWidthPx = doorScale?.averageDoorWidthPx ?? null;

  if (process.env.NODE_ENV === 'development') {
    console.log('CUBICASA CONVERTER SCALE', {
      rawPointsCount: rawPoints.length,
      rawWallsCount: rawWalls.length,
      rawOpeningsCount: rawOpenings.length,
      coordinateSystem,
      firstRawPoint: rawPoints[0],
      maxRawCoord,
      validDoorWidths,
      averageDoorWidthPx,
      pixelsPerMeter,
      metersPerPixel,
      scaleSource,
      expectedImportedWidthM: imageWidth / pixelsPerMeter,
      expectedImportedHeightM: imageHeight / pixelsPerMeter,
    });
  }

  const importedPoints = rawPoints.map((point) =>
    pixelPointToDesignerPoint(point, offsetXPx, offsetYPx),
  );
  const pointIds = new Set(importedPoints.map((point) => point.id));
  const validWalls = rawWalls
    .filter(
      (wall) =>
        pointIds.has(wall.startPointId) &&
        pointIds.has(wall.endPointId) &&
        wall.startPointId !== wall.endPointId,
    )
    .map(normalizeWallForDesigner);

  const openings = [];
  const pointBounds = getPointBounds(importedPoints);
  const meterBounds = getMeterBoundsFromPointBounds(pointBounds, metersPerPixel);

  if (process.env.NODE_ENV === 'development') {
    console.log('CUBICASA CONVERTER OUTPUT', {
      pointBoundsM: meterBounds,
      pointBounds,
      firstImportedPoint: importedPoints[0],
      importedPoints: importedPoints.length,
      importedWalls: validWalls.length,
    });
  }

  return {
    points: importedPoints,
    walls: validWalls,
    openings,
    rawOpenings,
    scale: {
      unit: 'm',
      pixelsPerMeter,
      metersPerPixel,
      source: scaleSource,
      standardDoorWidthM,
      averageDoorWidthPx,
      detectedDoorCount: doorScale?.detectedDoorCount ?? 0,
      importedWidthM: imageWidth / pixelsPerMeter,
      importedHeightM: imageHeight / pixelsPerMeter,
      rawDetectedOpeningsCount: rawOpenings.length,
      coordinateBounds: pointBounds,
      meterBounds,
      offsetXM: offsetX,
      offsetYM: offsetY,
      offsetXPx,
      offsetYPx,
    },
    canvasWidth: imageWidth + offsetXPx * 2,
    canvasHeight: imageHeight + offsetYPx * 2,
  };
}
